import React from "react";

import { formatIssueTime } from "../../utils/composedForecastUtils";
import { ACCURACY_METRICS, formatMetric } from "../../utils/forecastAccuracy";
import type { AccuracyConfig, AccuracyResult } from "../../utils/forecastAccuracy";

interface ForecastAccuracySummaryProps {
  config: AccuracyConfig;
  result: AccuracyResult | null;
  observationName: string;
  /** When the observation is another model: which of its issues was scored against. */
  observationIssue?: string;
  unit?: string;
  loading?: boolean;
  error?: string | null;
}

/** Summary metrics for the selected forecast issue against the observation. */
const ForecastAccuracySummary: React.FC<ForecastAccuracySummaryProps> = ({
  config,
  result,
  observationName,
  observationIssue,
  unit,
  loading = false,
  error = null,
}) => {
  const compared =
    config.mode === "band"
      ? `p${config.band?.[0] ?? 25}–p${config.band?.[1] ?? 75} band`
      : config.series ?? "mean";
  const metrics = ACCURACY_METRICS.filter((m) => config.metrics.includes(m.id));

  return (
    <div className="mt-3 rounded-md border border-eai-border bg-eai-surface p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-inter text-eai-body-sm font-medium text-eai-ink">
          Accuracy · {compared} vs {observationName}
          {observationIssue && (
            <span className="font-normal text-eai-ink-subtle">
              {" "}
              (its issue of {formatIssueTime(observationIssue)})
            </span>
          )}
        </div>
        {result && !loading && (
          <div className="font-inter text-eai-caption text-eai-ink-subtle">{result.matched} matched steps</div>
        )}
      </div>

      {loading && <div className="font-inter text-eai-body-sm text-eai-ink-subtle">Scoring this issue…</div>}
      {error && !loading && <div className="font-inter text-eai-body-sm text-eai-danger-text">{error}</div>}

      {result && !loading && !error && (
        <>
          {result.matched > 0 && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              {metrics.map((def) => (
                <div key={def.id} title={def.description}>
                  <dt className="font-inter text-eai-caption text-eai-ink-subtle">{def.label}</dt>
                  <dd className="font-inter text-eai-body-sm font-medium text-eai-ink">
                    {formatMetric(def, result.summary[def.id], unit)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {result.warnings.map((w) => (
            <div key={w} className="mt-2 font-inter text-eai-caption text-eai-ink-subtle">
              {w}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ForecastAccuracySummary;
